/**
 * Schema-driven form validation (required, length, phone, custom rules).
 */

import { useState, useCallback, useMemo } from 'react';
import { validatePhoneByCountry, getPhoneFormat } from '@/utils/phone-formats-utils';
import { getErrorMessage } from '@/utils/errors-utils';

/** Per-field rule (required, length, phone, custom, attendance). */
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  email?: boolean;
  phone?: boolean;
  url?: boolean;
  custom?: (value: any, formData?: any) => string | undefined;
  dependsOn?: string[];
  attendanceValidation?: boolean;
  phoneCountryCode?: string;
}

/** Live validation state for one field. */
export interface FieldValidation {
  isValid: boolean;
  isTouched: boolean;
  isDirty: boolean;
  error?: string;
}

/** Map of field path → {@link ValidationRule}. */
export interface ValidationSchema {
  [fieldName: string]: ValidationRule;
}

/** API of {@link useFormValidation}. */
export interface UseFormValidationReturn {
  fields: Record<string, FieldValidation>;
  validateField: (fieldName: string, value: any, formData?: any) => boolean;
  validateForm: (formData: any) => boolean;
  validateInitialFormData: (formData: any) => void;
  isFormValid: () => boolean;
  markAsTouched: (fieldName: string) => void;
  markAsDirty: (fieldName: string) => void;
  resetValidation: () => void;
  getFieldState: (fieldName: string) => 'default' | 'error' | 'success';
  getFieldError: (fieldName: string) => string | undefined;
}

/** Loose http(s) URL check used by form validation. */
const URL_REGEX = /^https?:\/\/.+/;

/** Lightweight email check (single `@`, domain has a dot). */
function isValidEmail(value: string): boolean {
  const at = value.indexOf('@');
  if (at <= 0 || at !== value.lastIndexOf('@')) return false;
  const domain = value.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  return !value.includes(' ') && dot > 0 && dot < domain.length - 1;
}

/** Required-field error (empty string / empty array). */
function requiredError(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return getErrorMessage('fieldRequired');
  }
  if (Array.isArray(value) && value.length === 0) {
    return getErrorMessage('atLeastOneSelectionRequired');
  }
  return undefined;
}

/** Phone length/format error using `_phoneCountryCode` or `rule.phoneCountryCode`. */
function phoneRuleError(value: string, rule: ValidationRule, formData?: any): string | undefined {
  const countryCode = (formData?._phoneCountryCode as string) || rule.phoneCountryCode || 'FR';
  const digitsOnly = value.replaceAll(/\D/g, '');
  const format = getPhoneFormat(countryCode);
  if (!format) return getErrorMessage('invalidPhone');
  if (digitsOnly.length < format.minLength) {
    return getErrorMessage({ code: 'phoneMinDigits', minLength: format.minLength });
  }
  if (digitsOnly.length > format.maxLength) {
    return getErrorMessage({ code: 'phoneMaxDigits', maxLength: format.maxLength });
  }
  if (!validatePhoneByCountry(value, countryCode)) {
    return getErrorMessage('invalidPhoneCountry');
  }
  return undefined;
}

/** Length / email / phone / url / pattern errors for string values. */
function stringRuleError(value: string, rule: ValidationRule, formData?: any): string | undefined {
  if (rule.minLength && value.length < rule.minLength) {
    return getErrorMessage({ code: 'minLength', minLength: rule.minLength });
  }
  if (rule.maxLength && value.length > rule.maxLength) {
    return getErrorMessage({ code: 'maxLength', maxLength: rule.maxLength });
  }
  if (rule.email && !isValidEmail(value)) return getErrorMessage('invalidEmail');
  if (rule.phone) return phoneRuleError(value, rule, formData);
  if (rule.url && !URL_REGEX.test(value)) return getErrorMessage('invalidUrl');
  if (rule.pattern && !rule.pattern.test(value)) return getErrorMessage('invalidFormat');
  return undefined;
}

/** Min/max errors for numeric values. */
function numberRuleError(value: unknown, rule: ValidationRule): string | undefined {
  const isNumeric = typeof value === 'number' || (value && !Number.isNaN(Number(value)));
  if (!isNumeric) return undefined;
  const numValue = typeof value === 'number' ? value : Number(value);
  if (rule.min !== undefined && numValue < rule.min) {
    return getErrorMessage({ code: 'minValue', min: rule.min });
  }
  if (rule.max !== undefined && numValue > rule.max) {
    return getErrorMessage({ code: 'maxValue', max: rule.max });
  }
  return undefined;
}

/** Absence reason required when status is `absent` (`other` needs a note). */
function attendanceRuleError(value: any): string | undefined {
  if (value.attendanceStatus !== 'absent') return undefined;
  if (!value.absenceReason) return getErrorMessage('absenceReasonRequired');
  if (value.absenceReason === 'other' && !value.absenceJustification?.trim()) {
    return getErrorMessage('specifyReason');
  }
  return undefined;
}

/** Schema-driven form validation with optional per-field debounce. */
export const useFormValidation = (
  schema: ValidationSchema,
  debounceMs: number = 0,
): UseFormValidationReturn => {
  const [fields, setFields] = useState<Record<string, FieldValidation>>(() => {
    const initialFields: Record<string, FieldValidation> = {};
    Object.keys(schema).forEach((fieldName) => {
      initialFields[fieldName] = {
        isValid: !schema[fieldName].required,
        isTouched: false,
        isDirty: false,
        error: undefined,
      };
    });
    return initialFields;
  });

  const [debounceTimers, setDebounceTimers] = useState<
    Record<string, ReturnType<typeof setTimeout>>
  >({});

  const validateFieldValue = useCallback(
    (fieldName: string, value: any, formData?: any): { isValid: boolean; error?: string } => {
      const rule = schema[fieldName];
      if (!rule) return { isValid: true };

      const reqError = rule.required ? requiredError(value) : undefined;
      if (reqError) return { isValid: false, error: reqError };

      const strError =
        value && typeof value === 'string' ? stringRuleError(value, rule, formData) : undefined;
      if (strError) return { isValid: false, error: strError };

      const numError = numberRuleError(value, rule);
      if (numError) return { isValid: false, error: numError };

      const attError = rule.attendanceValidation && value ? attendanceRuleError(value) : undefined;
      if (attError) return { isValid: false, error: attError };

      const customError = rule.custom?.(value, formData);
      if (customError) return { isValid: false, error: customError };

      return { isValid: true };
    },
    [schema],
  );

  const validateField = useCallback(
    (fieldName: string, value: any, formData?: any): boolean => {
      const performValidation = () => {
        const { isValid, error } = validateFieldValue(fieldName, value, formData);

        setFields((prev) => ({
          ...prev,
          [fieldName]: {
            ...prev[fieldName],
            isValid,
            error,
          },
        }));

        const rule = schema[fieldName];
        if (rule?.dependsOn) {
          rule.dependsOn.forEach((dependentField) => {
            if (formData?.[dependentField] !== undefined) {
              const { isValid: depIsValid, error: depError } = validateFieldValue(
                dependentField,
                formData[dependentField],
                formData,
              );
              setFields((prev) => ({
                ...prev,
                [dependentField]: {
                  ...prev[dependentField],
                  isValid: depIsValid,
                  error: depError,
                },
              }));
            }
          });
        }

        Object.keys(schema).forEach((otherFieldName) => {
          if (otherFieldName !== fieldName) {
            const otherRule = schema[otherFieldName];
            if (otherRule?.dependsOn?.includes(fieldName)) {
              if (formData?.[otherFieldName] !== undefined) {
                const { isValid: depIsValid, error: depError } = validateFieldValue(
                  otherFieldName,
                  formData[otherFieldName],
                  formData,
                );
                setFields((prev) => ({
                  ...prev,
                  [otherFieldName]: {
                    ...prev[otherFieldName],
                    isValid: depIsValid,
                    error: depError,
                  },
                }));
              }
            }
          }
        });

        return isValid;
      };

      if (debounceMs > 0) {
        if (debounceTimers[fieldName]) {
          clearTimeout(debounceTimers[fieldName]);
        }

        const timer = setTimeout(performValidation, debounceMs);
        setDebounceTimers((prev) => ({ ...prev, [fieldName]: timer }));

        return true;
      } else {
        return performValidation();
      }
    },
    [validateFieldValue, schema, debounceMs, debounceTimers],
  );

  const validateForm = useCallback(
    (formData: any): boolean => {
      let isValid = true;

      Object.keys(schema).forEach((fieldName) => {
        let fieldValue;

        if (fieldName.includes('.')) {
          const parts = fieldName.split('.');
          fieldValue = parts.reduce((obj, key) => obj?.[key], formData);
        } else {
          fieldValue = formData[fieldName];
        }

        const fieldIsValid = validateField(fieldName, fieldValue, formData);
        if (!fieldIsValid) {
          isValid = false;
        }
      });

      // Mark all schema fields as touched so errors become visible in the UI
      setFields((prev) => {
        const updated = { ...prev };
        Object.keys(schema).forEach((fieldName) => {
          if (updated[fieldName]) {
            updated[fieldName] = { ...updated[fieldName], isTouched: true };
          }
        });
        return updated;
      });

      return isValid;
    },
    [schema, validateField],
  );

  const validateInitialFormData = useCallback(
    (formData: any): void => {
      const updatedFields: Record<string, FieldValidation> = {};

      Object.keys(schema).forEach((fieldName) => {
        let fieldValue;

        if (fieldName.includes('.')) {
          const parts = fieldName.split('.');
          fieldValue = parts.reduce((obj, key) => obj?.[key], formData);
        } else {
          fieldValue = formData[fieldName];
        }

        const { isValid, error } = validateFieldValue(fieldName, fieldValue, formData);

        updatedFields[fieldName] = {
          isValid,
          isTouched: false,
          isDirty: false,
          error,
        };
      });

      setFields(updatedFields);
    },
    [schema, validateFieldValue],
  );

  const isFormValid = useCallback((): boolean => {
    return Object.values(fields).every((field) => field.isValid);
  }, [fields]);

  const markAsTouched = useCallback((fieldName: string) => {
    setFields((prev) => ({
      ...prev,
      [fieldName]: {
        isValid: true,
        isDirty: false,
        ...prev[fieldName],
        isTouched: true,
      },
    }));
  }, []);

  const markAsDirty = useCallback((fieldName: string) => {
    setFields((prev) => ({
      ...prev,
      [fieldName]: {
        isValid: true,
        isTouched: false,
        ...prev[fieldName],
        isDirty: true,
      },
    }));
  }, []);

  const resetValidation = useCallback(() => {
    const resetFields: Record<string, FieldValidation> = {};
    Object.keys(schema).forEach((fieldName) => {
      resetFields[fieldName] = {
        isValid: !schema[fieldName].required,
        isTouched: false,
        isDirty: false,
        error: undefined,
      };
    });
    setFields(resetFields);
  }, [schema]);

  const getFieldState = useCallback(
    (fieldName: string): 'default' | 'error' | 'success' => {
      const field = fields[fieldName];
      if (!field?.isTouched) return 'default';
      return field.isValid ? 'success' : 'error';
    },
    [fields],
  );

  const getFieldError = useCallback(
    (fieldName: string): string | undefined => {
      const field = fields[fieldName];
      if (!field?.isTouched || !field.error) return undefined;
      return getErrorMessage(field.error);
    },
    [fields],
  );

  const isDirty = useMemo(() => {
    return Object.values(fields).some((field) => field.isDirty);
  }, [fields]);

  return {
    fields,
    validateField,
    validateForm,
    validateInitialFormData,
    isFormValid,
    markAsTouched,
    markAsDirty,
    resetValidation,
    getFieldState,
    getFieldError,
    isDirty,
  } as UseFormValidationReturn & { isDirty: boolean };
};
